import { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTour } from '@/contexts/TourContext';
import { useTourFocusTrap } from './useTourFocusTrap';
import type { Step } from './types';
import { cn } from '@/lib/utils';
import { useTourState } from '@/hooks/useTourState';

// Re-export Step so `import { Step } from "@/components/tour/PageTour"` continues to work for any caller that used that path.
export type { Step };

interface PageTourProps {
  steps: Step[];
  /**
   * Optional tour identifier. When provided, exit-path status tracking is engaged
   * via useTourState. Omit for backwards-compat with existing consumers — wired
   * per-page in plan 14-06.
   *
   * D-07/D-06 reconciliation: D-07 says "writes status='seen' on completion".
   * D-06 supersedes with a 3-state exit: auto-launch writes the status matching
   * the user's actual exit path (Done→completed, Esc/Skip→dismissed, navigate-away→seen).
   */
  tourId?: string;
}

const CARD_WIDTH = 380;
const CARD_GAP = 12;
const TARGET_RETRY_ATTEMPTS = 15;
const TARGET_RETRY_INTERVAL_MS = 100;
const MOBILE_BREAKPOINT = 640;

type Placement = 'top' | 'bottom' | 'left' | 'right' | 'center';

function computePlacement(
  targetRect: DOMRect,
  cardRect: { width: number; height: number },
  viewport: { width: number; height: number },
  preferred: Placement = 'bottom'
): { top: number; left: number; placement: Placement } {
  // Try preferred placement; flip to opposite side if off-screen.
  const tryBottom = () => ({
    top: targetRect.bottom + CARD_GAP,
    left: Math.max(CARD_GAP, Math.min(targetRect.left, viewport.width - cardRect.width - CARD_GAP)),
    placement: 'bottom' as const,
  });
  const tryTop = () => ({
    top: targetRect.top - cardRect.height - CARD_GAP,
    left: Math.max(CARD_GAP, Math.min(targetRect.left, viewport.width - cardRect.width - CARD_GAP)),
    placement: 'top' as const,
  });
  const tryRight = () => ({
    top: Math.max(CARD_GAP, targetRect.top),
    left: targetRect.right + CARD_GAP,
    placement: 'right' as const,
  });
  const tryLeft = () => ({
    top: Math.max(CARD_GAP, targetRect.top),
    left: targetRect.left - cardRect.width - CARD_GAP,
    placement: 'left' as const,
  });

  const candidates = preferred === 'top' ? [tryTop, tryBottom, tryRight, tryLeft] :
                     preferred === 'right' ? [tryRight, tryLeft, tryBottom, tryTop] :
                     preferred === 'left' ? [tryLeft, tryRight, tryBottom, tryTop] :
                                            [tryBottom, tryTop, tryRight, tryLeft];
  for (const fn of candidates) {
    const pos = fn();
    if (pos.top >= 0 && pos.left >= 0 && pos.top + cardRect.height <= viewport.height && pos.left + cardRect.width <= viewport.width) {
      return pos;
    }
  }
  // Fallback: center
  return {
    top: Math.max(CARD_GAP, (viewport.height - cardRect.height) / 2),
    left: Math.max(CARD_GAP, (viewport.width - cardRect.width) / 2),
    placement: 'center',
  };
}

export function PageTour({ steps, tourId }: PageTourProps) {
  const { running, stopTour, stepIndex, setStepIndex } = useTour();
  const reduceMotion = useReducedMotion();
  const cardRef = useRef<HTMLDivElement>(null);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0, isMobile: false });
  const [cardPos, setCardPos] = useState<{ top: number; left: number; placement: Placement } | null>(null);

  // Exit-path status tracking (D-06 3-state exit). Only active when tourId is provided.
  // '__unused__' sentinel is used when tourId is absent so the hook call is unconditional
  // (Rules of Hooks forbid conditional hook calls). The sentinel never matches a real tour.
  const tourState = useTourState(tourId ?? '__unused__');
  // Track whether an explicit exit gesture already wrote status, so the cleanup
  // effect doesn't double-write 'seen' over a completed/dismissed status.
  const outcomeRef = useRef<'completed' | 'dismissed' | null>(null);

  const step = steps[stepIndex];
  const isCenterStep = !step || step.target === 'body' || step.placement === 'center';

  useTourFocusTrap(cardRef, running);

  // Viewport tracking
  useEffect(() => {
    const update = () => setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
      isMobile: window.innerWidth < MOBILE_BREAKPOINT,
    });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Resolve target element with retry (Pitfall 1)
  useEffect(() => {
    if (!running || isCenterStep || !step) {
      setTargetEl(null);
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const tryResolve = () => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(step.target);
      if (el) {
        setTargetEl(el);
        // Mobile: auto-scroll target into top half (Pitfall 8)
        if (viewport.isMobile) {
          el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        } else {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        return;
      }
      attempts++;
      if (attempts < TARGET_RETRY_ATTEMPTS) {
        setTimeout(tryResolve, TARGET_RETRY_INTERVAL_MS);
      } else {
        console.warn(`[PageTour] target not found after retry: ${step.target}`);
        setTargetEl(null);
      }
    };
    tryResolve();
    return () => { cancelled = true; };
  }, [running, step, isCenterStep, viewport.isMobile]);

  // Compute card position on target resolve / viewport change
  useLayoutEffect(() => {
    if (!running || isCenterStep || !targetEl || viewport.isMobile) {
      setCardPos(null);
      return;
    }
    const targetRect = targetEl.getBoundingClientRect();
    const cardRect = cardRef.current?.getBoundingClientRect() ?? { width: CARD_WIDTH, height: 200 };
    setCardPos(computePlacement(
      targetRect,
      { width: cardRect.width || CARD_WIDTH, height: cardRect.height || 200 },
      viewport,
      (step?.placement ?? 'bottom') as Placement
    ));
  }, [running, isCenterStep, targetEl, viewport.width, viewport.height, viewport.isMobile, step?.placement]);

  // Unmount-while-running cleanup: if tour is still running when component unmounts
  // (route change mid-tour) and no explicit exit gesture has fired, write 'seen'.
  // D-06/D-07 reconciliation: navigate-away→seen (not 'completed' per D-07 literal).
  useEffect(() => {
    return () => {
      if (running && tourId && outcomeRef.current === null) {
        tourState.markSeen();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, tourId]);

  // Keyboard nav
  const handleNext = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      // Done — last step advance: write completed before stopping (D-06 Done→completed).
      if (tourId) {
        outcomeRef.current = 'completed';
        tourState.markCompleted();
      }
      stopTour();
    } else {
      setStepIndex(stepIndex + 1);
    }
  }, [stepIndex, steps.length, setStepIndex, stopTour, tourId, tourState]);

  const handleBack = useCallback(() => {
    setStepIndex(Math.max(0, stepIndex - 1));
  }, [stepIndex, setStepIndex]);

  // Dismiss helper: writes dismissed before stopping (D-06 Esc/Skip→dismissed).
  const handleDismiss = useCallback(() => {
    if (tourId) {
      outcomeRef.current = 'dismissed';
      tourState.markDismissed();
    }
    stopTour();
  }, [tourId, tourState, stopTour]);

  useEffect(() => {
    if (!running) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleBack();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleDismiss();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [running, handleNext, handleBack, handleDismiss]);

  if (!running || !steps.length || !step) return null;

  const titleId = `tour-step-title-${stepIndex}`;
  const isLast = stepIndex === steps.length - 1;
  const total = steps.length;
  const spotlightRect = targetEl?.getBoundingClientRect();

  // Card positioning
  const cardStyle: React.CSSProperties = viewport.isMobile ? {
    position: 'fixed', bottom: 16, left: 16, right: 16, zIndex: 10001,
  } : isCenterStep || !cardPos ? {
    position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: CARD_WIDTH, zIndex: 10001,
  } : {
    position: 'fixed', top: cardPos.top, left: cardPos.left, width: CARD_WIDTH, zIndex: 10001,
  };

  const entranceProps = reduceMotion
    ? { initial: false as const }
    : { initial: { scale: 0.95, opacity: 0 }, animate: { scale: 1, opacity: 1 }, transition: { duration: 0.2, ease: 'easeOut' as const } };

  // On desktop with a resolved spotlight, the spotlight's box-shadow provides the
  // dim-around effect on its own — rendering an additional full-viewport backdrop
  // would double-dim the target and make the "highlight" indistinguishable. Only
  // render the full backdrop for center steps, missing targets, or mobile (which
  // uses a simple ring without a cutout).
  const showFullBackdrop = isCenterStep || !spotlightRect || viewport.isMobile;

  return (
    <>
      {/* Backdrop — skipped when spotlight box-shadow is covering the screen */}
      {showFullBackdrop && (
        <div
          className="fixed inset-0 bg-background/65 pointer-events-auto"
          style={{ zIndex: 10000 }}
          aria-hidden="true"
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Spotlight ring (desktop + when targetEl present + not center step) */}
      {!isCenterStep && spotlightRect && !viewport.isMobile && (
        <div
          className="fixed pointer-events-none rounded-md ring-[3px] ring-secondary"
          style={{
            top: spotlightRect.top - 4,
            left: spotlightRect.left - 4,
            width: spotlightRect.width + 8,
            height: spotlightRect.height + 8,
            zIndex: 10000,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.65), 0 0 24px 2px hsl(var(--secondary) / 0.35)',
          }}
          aria-hidden="true"
        />
      )}

      {/* Mobile spotlight ring on target (no cutout; just a highlight) */}
      {!isCenterStep && spotlightRect && viewport.isMobile && (
        <div
          className="fixed pointer-events-none ring-[3px] ring-secondary rounded-md"
          style={{
            top: spotlightRect.top - 4,
            left: spotlightRect.left - 4,
            width: spotlightRect.width + 8,
            height: spotlightRect.height + 8,
            zIndex: 10001,
            boxShadow: '0 0 24px 2px hsl(var(--secondary) / 0.35)',
          }}
          aria-hidden="true"
        />
      )}

      {/* Tour card */}
      <motion.div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'bg-surface-elevated border border-border rounded-lg shadow-[0_20px_40px_rgba(0,0,0,0.4)] p-6',
          'focus:outline-none'
        )}
        style={cardStyle}
        {...entranceProps}
      >
        <div className="flex items-start justify-between mb-1">
          {step.title && <h2 id={titleId} className="text-lg font-semibold text-foreground">{step.title}</h2>}
          <Button
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 -mt-1 -mr-2"
            onClick={handleDismiss}
            aria-label="Close tour"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        {!step.title && <h2 id={titleId} className="sr-only">Step {stepIndex + 1}</h2>}
        <p className="text-sm font-normal leading-relaxed text-foreground">{step.content}</p>
        <div className={cn('mt-4 flex items-center justify-between gap-2', viewport.isMobile && 'flex-col items-stretch')}>
          <span className="text-xs font-normal text-muted-foreground" aria-label={`Step ${stepIndex + 1} of ${total}`}>
            {stepIndex + 1} of {total}
          </span>
          <div className={cn('flex gap-2', viewport.isMobile && 'w-full')}>
            {stepIndex > 0 && (
              <Button variant="ghost" onClick={handleBack} className="text-sm font-normal min-h-11">
                Back
              </Button>
            )}
            <Button variant="ghost" onClick={handleDismiss} className="text-sm font-normal text-muted-foreground min-h-11">
              Skip tour
            </Button>
            <Button onClick={handleNext} className="text-sm font-semibold min-h-11">
              {isLast ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>
      </motion.div>
    </>
  );
}
